import { t } from '@lingui/macro'
import { Outlet, useLocation } from 'react-router-dom-original'
import styled from 'styled-components'

import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { DocumentExternalLinkSection } from '../../components/document-external-link-section/DocumentExternalLinkSection'
import { DocumentList } from '../../components/document-list/DocumentList'

import type { PatientDocumentsFilterOption, PatientViewDocumentsByYear } from '../../types/patient-documents.types'

type Props = {
  selectedFilter: PatientDocumentsFilterOption
  documentsByYear: PatientViewDocumentsByYear
  isLoading: boolean
  selectedDocumentId?: string
  unreadFiles?: number
  setSelectedFilter: (filter: PatientDocumentsFilterOption) => void
  filterMetadataMap: Map<PatientDocumentsFilterOption, number>
  handleDocumentClick: (documentId: string) => void
}

export const DocumentsDesktopOverview = ({
  selectedFilter,
  setSelectedFilter,
  documentsByYear,
  isLoading,
  unreadFiles,
  selectedDocumentId,
  filterMetadataMap,
  handleDocumentClick,
}: Props) => {
  const location = useLocation()

  const isPreviewRoute = location.pathname.includes('/preview/')

  return (
    <ContentPageLayout noScroll noPadding animationkey='documents-desktop-overview'>
      <StContainer>
        <StColumnContainer>
          <StScrollableContent>
            <StDocumentList
              incomingFilesByYear={documentsByYear}
              selectedDocumentId={selectedDocumentId}
              isLoading={isLoading}
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
              onDocumentClick={handleDocumentClick}
              filterMetadataMap={filterMetadataMap}
            />
          </StScrollableContent>
          <StDocumentExternalLinkSection unreadFiles={unreadFiles} />
        </StColumnContainer>
        <StColumnContainer>
          {selectedDocumentId || isPreviewRoute ? (
            <Outlet context={{ selectedDocumentId }} />
          ) : (
            <StNoDocumentSelectedContainer>
              <StNoDocumentSelectedImage src='/img/no-item-selected.png' alt='No document selected' />
              <H3 style={{ fontSize: '14px' }}>
                {t({
                  id: 'document-detail.noDocumentSelected',
                  message: 'Selecteer een document links in de lijst',
                })}
              </H3>
            </StNoDocumentSelectedContainer>
          )}
        </StColumnContainer>
      </StContainer>
    </ContentPageLayout>
  )
}

const StContainer = styled(Flex)`
  overflow: hidden;
  flex: 1;
  height: 100%;
`

const StColumnContainer = styled(FlexColumn)`
  flex: 1;
  height: 100%;

  &:nth-child(1) {
    overflow: hidden;
    padding: ${({ theme }) => theme.UI.SpacingPx.Space6};

    @media ${breakpoints.m} {
      padding-left: ${({ theme }) => theme.UI.SpacingPx.Space12};
    }
    @media ${breakpoints.l} {
      padding-left: ${({ theme }) => theme.UI.SpacingPx.Space20};
    }
  }

  &:nth-child(2) {
    border-left: 1px solid ${({ theme }) => theme.Components.Card.Border};
    overflow-y: auto;
  }
`

const StScrollableContent = styled(FlexColumn)`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
`

const StDocumentList = styled(DocumentList)`
  flex: 1;
`

const StDocumentExternalLinkSection = styled(DocumentExternalLinkSection)`
  flex-shrink: 0;
`

const StNoDocumentSelectedContainer = styled(FlexColumn)`
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 100%;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StNoDocumentSelectedImage = styled.img`
  max-width: 220px;
  width: 50%;
  opacity: 0.5;
`
